import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { AreaTrend } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { ShieldCheck, TrendingUp, Landmark } from "lucide-react";

export default function Save() {
  const { refreshUser } = useApp();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [sweep, setSweep] = useState(false);
  const [threshold, setThreshold] = useState(1000);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/save"); setData(data); setSweep(data.sweep_rule?.enabled); setThreshold(data.sweep_rule?.threshold_usd || 1000); };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  const move = async (direction) => {
    setBusy(true);
    try { await api.post("/save/move", { amount: Number(amount), direction });
      toast.success(direction === "deposit" ? "Moved to savings" : "Withdrawn to wallet", { description: fmtUSD(amount) });
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error("Failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  const saveRule = async (enabled, th) => { await api.post("/save/rules", { enabled, threshold_usd: Number(th) }); toast.success("Sweep rule updated"); };

  return (
    <AppShell>
      <PageHeader title="Save" subtitle="USD high-yield account · daily accrual" testid="save-header" />
      {!data ? <EmptyState title="Loading..." /> : (
        <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
          <Card className="p-6 shadow-premium card-premium lg:col-span-2">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Savings Balance</div>
                <div className="text-4xl font-semibold numeric mt-1" data-testid="save-balance">{fmtUSD(data.balance)}</div>
                <div className="text-sm text-muted-foreground mt-1">Total earned: <span className="numeric text-emerald-600">{fmtUSD(data.accrued_total)}</span></div>
              </div>
              <div className="text-end">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">APY</div>
                <div className="text-3xl font-semibold numeric text-emerald-600" data-testid="save-apy">{data.apy_pct}%</div>
                <div className="text-xs text-muted-foreground">Daily: {fmtUSD(data.daily_yield, 4)}</div>
              </div>
            </div>
            <div className="mt-6"><div className="text-sm font-medium mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4" />Yield History (30d)</div>
              <AreaTrend data={data.history} dataKey="balance" color="#5C7020" height={220} /></div>
          </Card>

          <div className="space-y-4 sm:space-y-5">
            <Card className="p-5 shadow-premium">
              <div className="text-sm font-medium mb-3">Move Money</div>
              <div className="space-y-2">
                <Label>Amount (USD)</Label>
                <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="save-amount-input" />
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button onClick={() => move("deposit")} disabled={busy} data-testid="save-deposit-button">Deposit</Button>
                  <Button variant="secondary" onClick={() => move("withdraw")} disabled={busy} data-testid="save-withdraw-button">Withdraw</Button>
                </div>
                <p className="text-xs text-muted-foreground">Funds move between your USD wallet and savings.</p>
              </div>
            </Card>
            <Card className="p-5 shadow-premium">
              <div className="flex items-center justify-between">
                <div><div className="text-sm font-medium">Auto-sweep</div><div className="text-xs text-muted-foreground">Sweep excess USD into yield</div></div>
                <Switch checked={sweep} onCheckedChange={(v) => { setSweep(v); saveRule(v, threshold); }} data-testid="save-sweep-toggle" />
              </div>
              {sweep && <div className="mt-3 space-y-1.5"><Label>Keep in wallet above (USD)</Label>
                <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} onBlur={() => saveRule(true, threshold)} /></div>}
            </Card>
            <Card className="p-5 shadow-premium">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><Landmark className="h-4 w-4" />Where your yield comes from</div>
              <div className="text-sm font-medium">{data.partner.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.partner.provider}</div>
              <div className="flex items-center gap-2 mt-2 text-xs"><ShieldCheck className="h-4 w-4 text-emerald-600" />{data.partner.rating} rated · {data.partner.sipc}</div>
            </Card>
          </div>
        </div>
      )}
    </AppShell>
  );
}
